import actionTypes from 'redux/actions/actionTypes'

import {
	topInformationGenerator,
	topInformationTypes,
	closeTopInformation,
} from 'redux/reducers/helpers/topInformationGenerator'
import questionStatus from 'helpers/enums/creationTool/questionStatus'
import { localize } from 'helpers/translator'
import answerTypes from 'helpers/enums/answerTypes'
import { getFlag } from 'helpers/featureFlags/provider'
import featureFlagsTypes from 'helpers/enums/featureFlagsTypes'

const MAX_ANSWERS = 15
const MIN_ANSWERS = 2

const defaultQuestion = {
	textTouched: true,
	touched: true,
	text: '',
	answers: [],
	status: questionStatus.NONE,
}

const defaultAnswer = {
	clientId: '',
	serverId: '',
	textTouched: true,
	media: {
		type: answerTypes.TEXT,
		payload: {
			text: '',
		},
	},
	correct: false,
}

const defaultState = {
	activityUid: '',
	question: defaultQuestion,
	isModified: false,
	topInformation: [],
}

function initialize(state, action) {
	return {
		...state,
		activityUid: action.activityUid || '',
	}
}

function setNewActivityData(state, action) {
	return {
		...state,
		activityUid: action.activityUid,
		question: {
			...defaultQuestion,
			answers: action.answersUids.map(answerUid => ({
				...defaultAnswer,
				clientId: answerUid,
				serverId: answerUid,
			})),
		},
	}
}

function setQuestionAndAnswers(state, action) {
	return {
		...state,
		question: {
			...defaultQuestion,
			text: action.payload.text,
			answers: action.payload.answers.map(answer => ({
				...defaultAnswer,
				clientId: answer.uid,
				media: answer.media,
				correct: !!answer.correct,
				serverId: answer.uid,
			})),
		},
	}
}

function updateAnswerServerIds(state, action) {
	const newState = changeQuestionStatus(state, questionStatus.NONE)
	return {
		...newState,
		question: {
			...newState.question,
			answers: newState.question.answers.map((answer, index) => ({
				...answer,
				serverId: action.answers[index].uid,
			})),
		},
	}
}

function changeQuestionStatus(state, status) {
	return {
		...state,
		isModified: true,
		question: {
			...state.question,
			status,
		},
	}
}

function handleQuestionChange(state, action) {
	const newState = changeQuestionStatus(state, questionStatus.CHANGED)
	return {
		...newState,
		question: {
			...newState.question,
			text: action.value,
			textTouched: true,
		},
	}
}

function handleAnswerChange(state, action) {
	return {
		...state,
		question: {
			...state.question,
			answers: state.question.answers.map(answer => {
				if (answer.clientId === action.answerId) {
					return {
						...answer,
						media: {
							...answer.media,
							payload: {
								...answer.media.payload,
								text: action.value,
							},
						},
						textTouched: true,
					}
				} else {
					return answer
				}
			}),
		},
	}
}

function validateAddAnswer(state, action) {
	if (state.question.answers.length < MAX_ANSWERS) {
		return addAnswer(state, action)
	} else {
		return {
			...state,
			topInformation: [
				topInformationGenerator({
					message: localize('mcq_answers_validation_maximum'),
					styleType: topInformationTypes.ERROR,
				}),
			],
		}
	}
}

function addAnswer(state, action) {
	const newState = changeQuestionStatus(state, questionStatus.SAVING)
	return {
		...newState,
		question: {
			...newState.question,
			answers: [
				...newState.question.answers,
				{
					...defaultAnswer,
					clientId: action.answerId,
				},
			],
		},
	}
}

function validateDeleteAnswer(state, action) {
	if (state.question.answers.length > MIN_ANSWERS) {
		return deleteAnswer(state, action)
	} else {
		return {
			...state,
			topInformation: [
				topInformationGenerator({
					message: localize('mcq_answers_validation_minimum'),
					styleType: topInformationTypes.ERROR,
				}),
			],
		}
	}
}

function deleteAnswer(state, action) {
	const newState = changeQuestionStatus(state, questionStatus.SAVING)
	return {
		...newState,
		question: {
			...newState.question,
			answers: newState.question.answers.filter(
				answer => answer.clientId !== action.answerId
			),
		},
	}
}

function moveAnswer(state, action) {

	const newState = {
		...state,
		question: {
			...state.question,
			answers: [...state.question.answers],
		},
		isModified: true,
	}

	// search dragged and replaced answers positions
	const draggedAnswerIndex = state.question.answers.findIndex(
		answer => answer.clientId === action.draggedAnswer.answerId
	)
	const replacedAnswerIndex = state.question.answers.findIndex(
		answer => answer.clientId === action.replacedAnswer.answerId
	)
	// move replaced to dragged position
	newState.question.answers[draggedAnswerIndex] = {
		...state.question.answers[replacedAnswerIndex],
	}
	// move dragged to replaced position
	newState.question.answers[replacedAnswerIndex] = {
		...state.question.answers[draggedAnswerIndex],
	}

	return newState
}

function answerCheckboxChange(state, action) {
	if (getFlag(featureFlagsTypes.MCQ_MULTIPLE_CORRECT_ANSWERS)) {
		return multipleCorrectAnswersToggle(state, action)
	} else {
		return singleCorrectAnswerToggle(state, action)
	}
}

function singleCorrectAnswerToggle(state, action) {
	return {
		...state,
		question: {
			...state.question,
			touched: true,
			answers: state.question.answers.map(answer => ({
				...answer,
				correct: answer.clientId === action.answerId
					? !answer.correct
					: false,
			})),
			status: questionStatus.SAVING,
		},
	}
}

function multipleCorrectAnswersToggle(state, action) {
	return {
		...state,
		question: {
			...state.question,
			touched: true,
			answers: state.question.answers.map(answer => {
				if (answer.clientId === action.answerId) {
					return {
						...answer,
						correct: !answer.correct,
					}
				} else {
					return answer
				}
			}),
			status: questionStatus.SAVING,
		},
	}
}

function closeTopInformationIfOnScope(state, uid) {
	return {
		...state,
		topInformation: closeTopInformation(state.topInformation, uid),
	}
}

function setSaveFailedState(state, action) {
	return {
		...state,
		question: {
			...state.question,
			textTouched: false,
			touched: false,
			answers: state.question.answers.map(answer => ({
				...answer,
				textTouched: false,
			})),
		},
		topInformation: [
			topInformationGenerator({
				message: action.error.message,
				styleType: topInformationTypes.ERROR,
			}),
		],
	}
}

function answerFocus(state, action) {
	return {
		...state,
		question: {
			...state.question,
			answers: state.question.answers.map(answer => {
				if (answer.clientId === action.answerId) {
					return {
						...answer,
						textTouched: true,
					}
				} else {
					return answer
				}
			}),
		},
	}
}

function questionFocus(state) {
	return {
		...state,
		question: {
			...state.question,
			textTouched: true,
		},
	}
}

function finalize() {
	return defaultState
}

const mcqReducer = (state = defaultState, action) => {
	switch (action.type) {
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_INITIALIZE:
		case actionTypes.VIDEO_CREATION_MCQ_INITIALIZE:
		case actionTypes.VIDEO_STANDALONE_MCQ_INITIALIZE:
			return initialize(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_CREATE_SUCCESS:
			return setNewActivityData(state, action)
		case actionTypes.CREATION_TOOL_MCQ_INITIALIZE_SUCCESS:
			return setQuestionAndAnswers(state, action)
		case actionTypes.CREATION_TOOL_MCQ_QUESTION_CHANGE:
			return handleQuestionChange(state, action)
		case actionTypes.CREATION_TOOL_MCQ_QUESTION_AUTOSAVE:
		case actionTypes.CREATION_TOOL_MCQ_ANSWER_AUTOSAVE:
			return changeQuestionStatus(state, questionStatus.SAVING)
		case actionTypes.CREATION_TOOL_MCQ_QUESTION_UPDATE_SUCCESS:
			return updateAnswerServerIds(state, action)
		case actionTypes.CREATION_TOOL_MCQ_ANSWER_CHANGE:
			return handleAnswerChange(state, action)
		case actionTypes.CREATION_TOOL_MCQ_ANSWER_ADD:
			return validateAddAnswer(state, action)
		case actionTypes.CREATION_TOOL_MCQ_ANSWER_DELETE:
			return validateDeleteAnswer(state, action)
		case actionTypes.CREATION_TOOL_MCQ_ANSWER_DRAG:
			return moveAnswer(state, action)
		case actionTypes.CREATION_TOOL_MCQ_ANSWER_CHECKBOX_CHANGE:
			return answerCheckboxChange(state, action)
		case actionTypes.CLOSE_TOP_INFORMATION:
			return closeTopInformationIfOnScope(state, action.uid)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_SAVE_ERROR:
			return setSaveFailedState(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_ANSWER_FOCUS:
			return answerFocus(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_QUESTION_FOCUS:
			return questionFocus(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_FINALIZE:
		case actionTypes.VIDEO_STANDALONE_MCQ_FINALIZE:
			return finalize()
		default: return state
	}
}
export default mcqReducer
